import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "sonner";
import { partnerApiService } from "@/services/partner-api.service";

/**
 * The billing portal session returns a one-time Stripe URL we need to open in
 * a new tab. Calling `window.open(url)` inside the mutation's `onSuccess`
 * fires outside the user gesture and gets cancelled by popup blockers, so we
 * synchronously open `about:blank` on click and navigate that already-open
 * tab once the mutation resolves (the admin impersonation pattern).
 */
export const useBillingPortal = () => {
	const pendingWindowRef = useRef<Window | null>(null);

	const mutation = useMutation({
		mutationFn: () => partnerApiService.createPortalSession(),
		onSuccess: (data) => {
			const w = pendingWindowRef.current;
			pendingWindowRef.current = null;
			if (w && !w.closed) {
				w.location.href = data.url;
			} else {
				const fallback = window.open(data.url, "_blank");
				if (!fallback) {
					toast.error(
						"Browser blocked the billing tab. Allow popups for this site and try again.",
					);
				}
			}
		},
		onError: () => {
			const w = pendingWindowRef.current;
			pendingWindowRef.current = null;
			if (w && !w.closed) w.close();
			toast.error("Failed to open the billing portal");
		},
	});

	const openPortal = () => {
		if (mutation.isPending) return;
		// CRITICAL: open the tab synchronously *inside* the click handler so the
		// browser counts it as a user-initiated popup.
		pendingWindowRef.current = window.open("about:blank", "_blank");
		mutation.mutate();
	};

	return Object.assign(mutation, { openPortal });
};
